import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { authService, setAuthTokenGetter, AUTH_UNAUTHORIZED_EVENT } from "@/services";
import {
  clearAuthToken,
  decodeJwtPayload,
  getAuthToken,
  payloadToAuthUser,
  setAuthToken,
} from "@/utils";
import type { AuthUser } from "@/types";
import { AuthContext } from "./auth.context";

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Provides authentication state and actions app-wide
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setAuthTokenGetter(() => token);
  }, [token]);

  useEffect(() => {
    const storedToken = getAuthToken();
    if (!storedToken) {
      setIsLoading(false);
      return;
    }

    const payload = decodeJwtPayload(storedToken);
    const authUser = payload ? payloadToAuthUser(payload) : null;

    if (authUser) {
      setToken(storedToken);
      setUser(authUser);
    } else {
      clearAuthToken();
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    const handleUnauthorized = (): void => {
      clearAuthToken();
      setToken(null);
      setUser(null);
    };

    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => {
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await authService.login({ email, password });
    const payload = decodeJwtPayload(response.access_token);
    const authUser = payload ? payloadToAuthUser(payload) : null;

    if (!authUser) {
      throw new Error("Invalid token payload");
    }

    setAuthToken(response.access_token);
    setToken(response.access_token);
    setUser(authUser);

    return authUser;
  }, []);

  const logout = useCallback(() => {
    clearAuthToken();
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      isLoading,
      isAuthenticated: Boolean(user && token),
      login,
      logout,
    }),
    [user, token, isLoading, login, logout],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};
